'use client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type ToastVariant = 'success' | 'error';

interface ToastContextValue {
  showToast: (message: string, variant?: ToastVariant) => void;
  hideToast: () => void;
}

interface ToastState {
  message: string;
  variant: ToastVariant;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

const TOAST_DURATION_MS = 5000;

export const ToastProvider = ({ children }: ToastProviderProps) => {
  const [toast, setToast] = useState<ToastState | null>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hideToast = useCallback(() => {
    if (dismissTimer.current) {
      clearTimeout(dismissTimer.current);
      dismissTimer.current = null;
    }

    setToast(null);
  }, []);

  const showToast = useCallback((message: string, variant: ToastVariant = 'success') => {
    if (dismissTimer.current) {
      clearTimeout(dismissTimer.current);
    }

    setToast({ message, variant });

    dismissTimer.current = setTimeout(() => {
      setToast(null);
      dismissTimer.current = null;
    }, TOAST_DURATION_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (dismissTimer.current) {
        clearTimeout(dismissTimer.current);
        dismissTimer.current = null;
      }
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      showToast,
      hideToast,
    }),
    [showToast, hideToast],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 bottom-6 flex justify-center px-4"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {toast && (
          <div
            className={`pointer-events-auto min-w-[260px] max-w-lg rounded-xl px-5 py-3 text-center text-sm font-medium text-white shadow-2xl transition-all ${
              toast.variant === 'success' ? 'bg-emerald-600' : 'bg-red-600'
            }`}
          >
            {toast.message}
          </div>
        )}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
